/**
 * Persistent profile documents at `users/{uid}`.
 *
 * Display data only. No OAuth tokens, no API keys, no provider credentials.
 * The document id is the Firebase uid, so the profile and the app identity can
 * never drift apart.
 */
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  buildProfileCreatePayload,
  buildProfileUpdatePayload,
} from '../utils/userProfile';
import { describeError } from '../utils/errorLog';

function profileRef(uid) {
  return doc(db, 'users', uid);
}

export async function fetchUserProfile(uid) {
  if (!uid) return null;
  const snapshot = await getDoc(profileRef(uid));
  if (!snapshot?.exists?.()) return null;
  return { uid, ...snapshot.data() };
}

/**
 * Create the profile on first sign-in, otherwise refresh display fields.
 * Failures are non-fatal: a missing profile must never block joining a party.
 */
export async function upsertUserProfile(user, { displayName = null } = {}) {
  if (!user?.uid) return null;

  try {
    const existingSnapshot = await getDoc(profileRef(user.uid));
    const existing = existingSnapshot?.exists?.() ? existingSnapshot.data() : null;
    const timestamp = serverTimestamp();

    const payload = existing
      ? buildProfileUpdatePayload(user, { displayName, timestamp, existing })
      : buildProfileCreatePayload(user, { displayName, timestamp });

    if (!payload) return null;

    await setDoc(profileRef(user.uid), payload, { merge: true });
    return payload;
  } catch (error) {
    console.error('Failed to upsert user profile:', describeError(error));
    return null;
  }
}
